import React from 'react';
import {func} from 'prop-types';
import {intlShape} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {types as sdkTypes} from '../../../../util/sdkLoader';
import {createSlug} from '../../../../util/urlHelpers';

import {withViewport} from '../../../../util/contextHelpers';
import {propTypes} from '../../../../util/types';
import {ensureTransaction} from '../../../../util/data';
import {
  getProposalAmountWithCustomerFee,
  getProposalAmountWithoutProviderFee,
  getUserTxRole,
  TRANSITION_COUNTER_PROPOSAL_ACCEPT,
  TRANSITION_PROPOSAL_ACCEPT,
  TRANSITION_REQUEST_PAYMENT_AFTER_PROPOSAL,
  txRoleIsProvider,
} from '../../../../util/transactions';
import {PrimaryButton, IconCheckCircle, UserDisplayName} from '../../../../components';

import {useHistory} from 'react-router-dom';

import css from './Confirm.module.css';
import routeConfiguration from '../../../../routeConfiguration';
import {
  CHECKOUT_EXTRA_PAGE,
  CHECKOUT_PAGE,
  CHECKOUT_PROPOSAL_PAGE,
} from '../../../../constants/page';
import {createResourceLocatorString, findRouteByRouteName} from '../../../../util/routes';
import {pathOr} from 'ramda';
import {formatMoney, unitDivisor} from '../../../../util/currency';
import {moment} from '../../../../util/dates';
import {initializeCardPaymentData} from '../../../../ducks/stripe.duck';
import {formatFormValuesAsBookingData} from '../../../../util/booking';
import {getTxType} from "../../../../containers/InboxPage/InboxPage.Helpers";

const {Money} = sdkTypes;

const Confirm = (props) => {
  const {transaction, currentUser, intl, onInitializeCardPaymentData, callSetInitialValues} =
    props;
  const txType = getTxType(transaction, currentUser);
  const isOrder = txType === 'order';
  const history = useHistory();
  const currentTransaction = ensureTransaction(transaction);
  const lastTransition = currentTransaction.attributes.lastTransition;
  const listing = currentTransaction.listing;
  const publicData = pathOr({}, ['attributes', 'publicData'], listing);
  const protectedData = pathOr({}, ['attributes', 'protectedData'], currentTransaction);
  const {paidExtrasData = []} = publicData;

  const proposalPriceAmount =
    lastTransition === TRANSITION_PROPOSAL_ACCEPT
      ? protectedData.proposalPriceAmount
      : protectedData.counterProposalPriceAmount;

  const confirmMessage = intl.formatMessage({id: 'ActivityFeed.pendingProposalConfirmBooking'});
  const proposalAcceptedMessage = intl.formatMessage(
    {
      id: [TRANSITION_PROPOSAL_ACCEPT, TRANSITION_REQUEST_PAYMENT_AFTER_PROPOSAL].includes(
        lastTransition
      )
        ? 'ActivityFeed.yourProposalAccepted'
        : 'ActivityFeed.ownTransitionCounterProposalAccept',
    },
    {
      counterProposalPrice: protectedData.counterProposalPriceAmount
        ? formatMoney(
          intl,
          new Money(!isOrder ? getProposalAmountWithoutProviderFee(protectedData.counterProposalPriceAmount) : getProposalAmountWithCustomerFee(protectedData.counterProposalPriceAmount), protectedData.proposalPriceCurrency)
        )
        : '',
      proposalPrice: protectedData.proposalPriceAmount
        ? formatMoney(
          intl,
          new Money(!isOrder ? getProposalAmountWithoutProviderFee(protectedData.proposalPriceAmount) : getProposalAmountWithCustomerFee(protectedData.proposalPriceAmount), protectedData.proposalPriceCurrency)
        )
        : '',
    }
  );

  const handleClickConfirm = () => {
    const bookingData = {
      bookingStart: moment(protectedData.bookingStartDisplay),
      bookingEnd: moment(protectedData.bookingEndDisplay),
      quantity: protectedData.quantity,
      quantityHours: moment(protectedData.bookingEndDisplay).diff(
        moment(protectedData.bookingStartDisplay),
        'hours'
      ),
    };
    const initialValues = {
      listing,
      transaction,
      bookingData: {
        ...bookingData,
        proposalPriceAmount,
        proposalPriceCurrency: protectedData.proposalPriceCurrency,
      },
      bookingDates: {
        bookingStart: bookingData.bookingStart.toDate(),
        bookingEnd: bookingData.bookingEnd.toDate(),
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !currentUser;
    const routes = routeConfiguration();
    const listingHasExtras = paidExtrasData.length > 0;
    const nextRouteName = listingHasExtras ? CHECKOUT_EXTRA_PAGE : CHECKOUT_PAGE;

    const {setInitialValues} = findRouteByRouteName(nextRouteName, routes);
    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

    if (!listingHasExtras) {
      onInitializeCardPaymentData();
    }

    history.push(
      createResourceLocatorString(
        nextRouteName,
        routes,
        {id: listing.id.uuid, isProposal: true, slug: createSlug(listing.attributes.title)},
        {isProposal: 'true'}
      )
    );
  };

  return (
    <div className={css.confirm}>
      <IconCheckCircle className={css.confirmImg}/>
      <p className={css.confirmContent}>{proposalAcceptedMessage}</p>
      <div className={css.confirmButtons}>
        <PrimaryButton className={css.confirmDetailsButton} onClick={handleClickConfirm}>
          {confirmMessage}
        </PrimaryButton>
      </div>
    </div>
  );
};

Confirm.defaultProps = {
  transition: null,
  transaction: null,
  currentUser: null,
  intl: null,
};

Confirm.propTypes = {
  transition: propTypes.transition.isRequired,
  transaction: propTypes.transaction.isRequired,
  currentUser: propTypes.currentUser.isRequired,
  intl: intlShape.isRequired,
  onManageDisableScrolling: func.isRequired,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
});

const mapStateToProps = (state) => {
  const {id} = state.listing;
  const {currentUser} = state.user;

  return {
    currentUser,
    listingId: id,
  };
};

export default compose(withViewport, connect(mapStateToProps, mapDispatchToProps))(Confirm);
